import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { IAction, SliceProps } from '../../store';
import {
    urlParamReplicaId,
    urlProjectDocumentsTestmode,
    urlProjectDocumentsPark,
    urlProjectDocumentsStop,
    urlQueryParamState,
} from '../../../Constants';
import { apiFetch } from '../../../util/ProjectUtils';

export enum DocumentsStateDialogMode {
    Testmode,
    Parking,
    Stopping,
}

export interface IProjectDocumentsActivationState extends SliceProps {
    isDialogOpen: boolean;
    mode?: DocumentsStateDialogMode;
}

const initialState: IProjectDocumentsActivationState = {
    isDialogOpen: false,
    mode: undefined,
    loading: false,
    error: null,
};

const reducers = {
    showDocumentsStateDialog: (
        state: Draft<IProjectDocumentsActivationState>,
        action: IAction<DocumentsStateDialogMode>,
    ) => {
        state.mode = action.payload;
        state.isDialogOpen = true;
    },
    hideDocumentsStateDialog: (state: Draft<IProjectDocumentsActivationState>) => {
        state.isDialogOpen = false;
        state.mode = undefined;
    },
};

const urlMap = {
    [DocumentsStateDialogMode.Testmode]: urlProjectDocumentsTestmode,
    [DocumentsStateDialogMode.Parking]: urlProjectDocumentsPark,
    [DocumentsStateDialogMode.Stopping]: urlProjectDocumentsStop,
};

const thunks = {
    updateDocumentsActivation: async (params: {
        mode: DocumentsStateDialogMode;
        replicaId: string;
        documentIds: string[];
        status: boolean;
        description?: string;
    }): Promise<void> => {
        const { replicaId, documentIds, status, description, mode } = params;

        try {
            const url = urlMap[mode]
                .replace(urlParamReplicaId, replicaId)
                .replace(urlQueryParamState, status.toString());

            const form = new FormData();
            form.append('universalIds', documentIds.join('|'));
            description && form.append('options', JSON.stringify({ comment: description }));

            const request: RequestInit = {
                method: 'POST',
                body: form,
            };

            const response = await apiFetch(url, request);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }

            return Promise.resolve();
        } catch (error) {
            return Promise.reject(error as Error);
        }
    },

    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentsActivationState>) => {
        builder.addCase(
            updateDocumentsActivation.pending,
            (state: Draft<IProjectDocumentsActivationState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            updateDocumentsActivation.fulfilled,
            (state: Draft<IProjectDocumentsActivationState>) => {
                state.loading = false;
                state.error = null;
            },
        );
        builder.addCase(
            updateDocumentsActivation.rejected,
            (state: Draft<IProjectDocumentsActivationState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const updateDocumentsActivation = createAsyncThunk(
    'project/documents/activation',
    thunks.updateDocumentsActivation,
);

export const projectDocumentsActivationSlice = createSlice({
    name: 'projectDocumentsActivation',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export const { showDocumentsStateDialog, hideDocumentsStateDialog } =
    projectDocumentsActivationSlice.actions;

export default projectDocumentsActivationSlice.reducer;
